import React, { useState, useEffect } from 'react';
import { Search, Filter, Edit, Trash2, Ban, Crown, Check, X, Save, Mail, User, Shield } from 'lucide-react';
import { getAdminUsers, updateUserTier, freezeUser } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchUsers();
  }, [page, filterRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getAdminUsers({ page, limit: 20, role: filterRole !== 'all' ? filterRole : undefined });
      setUsers(res.data.users || res.data);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleFreeze = async (userId, freeze) => {
    try {
      await freezeUser(userId, freeze);
      toast.success(freeze ? 'User frozen' : 'User unfrozen');
      fetchUsers();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleUpdateTier = async (userId, tier) => {
    try {
      await updateUserTier(userId, tier);
      toast.success('Tier updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditForm({
      email: user.email,
      username: user.username,
      subscription_tier: user.subscription_tier || 'free',
      role: user.role || 'user',
      bio: user.bio || ''
    });
  };

  const handleSaveUser = async () => {
    try {
      await api.put(`/admin/users/${editingUser}/account`, editForm);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`⚠️ DELETE USER?\n\nThis will permanently delete:\n• User: ${username}\n• All their comics\n• All their content\n\nThis CANNOT be undone!`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`User ${username} deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getTierBadge = (tier) => {
    const colors = {
      free: 'bg-gray-500/20 text-gray-400',
      basic: 'bg-gray-500/20 text-gray-400',
      premium: 'bg-amber-500/20 text-amber-400',
      creator: 'bg-blue-500/20 text-blue-400',
      contributor: 'bg-purple-500/20 text-purple-400'
    };
    return colors[tier] || colors.free;
  };

  const getRoleBadge = (role) => {
    const colors = {
      user: 'bg-gray-500/20 text-gray-400',
      creator: 'bg-blue-500/20 text-blue-400',
      admin: 'bg-orange-500/20 text-orange-400',
      super_admin: 'bg-red-500/20 text-red-400'
    };
    return colors[role] || colors.user;
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-users">
      {/* Header & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
          >
            <option value="all">All Roles</option>
            <option value="user">Users</option>
            <option value="creator">Creators</option>
            <option value="admin">Admins</option>
          </select>
        </div>
        <div className="text-sm text-[var(--text-secondary)]">
          {filteredUsers.length} users
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--background)]">
              <tr className="text-left text-sm text-[var(--text-secondary)]">
                <th className="py-3 px-4">User</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Tier</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Joined</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-[var(--background)]/50">
                  {editingUser === user.id ? (
                    // Edit Mode
                    <>
                      <td className="py-3 px-4" colSpan={6}>
                        <div className="bg-[var(--background)] p-4 rounded-lg space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">Editing: {user.username}</h4>
                            <button onClick={() => setEditingUser(null)} className="p-1 hover:bg-[var(--surface)] rounded">
                              <X size={18} />
                            </button>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-[var(--text-secondary)] mb-1">Email</label>
                              <input
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-[var(--text-secondary)] mb-1">Username</label>
                              <input
                                type="text"
                                value={editForm.username}
                                onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                                className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-[var(--text-secondary)] mb-1">Subscription Tier</label>
                              <select
                                value={editForm.subscription_tier}
                                onChange={(e) => setEditForm({...editForm, subscription_tier: e.target.value})}
                                className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg text-sm"
                              >
                                <option value="free">Free</option>
                                <option value="premium">Premium</option>
                                <option value="creator">Creator</option>
                                <option value="contributor">Contributor</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-[var(--text-secondary)] mb-1">Role</label>
                              <select
                                value={editForm.role}
                                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                                className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg text-sm"
                              >
                                <option value="user">User</option>
                                <option value="creator">Creator</option>
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => setEditingUser(null)}
                              className="px-4 py-2 border border-[var(--border)] rounded-lg text-sm hover:bg-[var(--surface)]"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveUser}
                              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm hover:bg-[var(--primary)]/90 flex items-center gap-2"
                            >
                              <Save size={14} /> Save Changes
                            </button>
                          </div>
                        </div>
                      </td>
                    </>
                  ) : (
                    // View Mode
                    <>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                            <span className="text-xs font-medium text-[var(--primary)]">
                              {user.username?.[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{user.username}</p>
                            <p className="text-xs text-[var(--text-secondary)]">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                          {user.role || 'user'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadge(user.subscription_tier)}`}>
                          {user.subscription_tier || 'free'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_frozen ? (
                          <span className="text-red-500 text-sm">Frozen</span>
                        ) : (
                          <span className="text-green-500 text-sm">Active</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 hover:bg-[var(--background)] rounded-lg text-[var(--text-secondary)] hover:text-[var(--primary)]"
                            title="Edit user"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleFreeze(user.id, !user.is_frozen)}
                            className={`p-2 hover:bg-[var(--background)] rounded-lg ${user.is_frozen ? 'text-green-500' : 'text-orange-500'}`}
                            title={user.is_frozen ? 'Unfreeze' : 'Freeze'}
                          >
                            <Ban size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="p-2 hover:bg-red-500/10 rounded-lg text-red-500"
                            title="Delete user"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border border-[var(--border)] rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 border border-[var(--border)] rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
